/**
 * Core Task Migration Tab (Epic 6 — Migratie & livegang, Story 6.3)
 *
 * Instellingen > Functieloonhuis > Migratie kerntaken (alleen rol `admin` —
 * afgedwongen op routeniveau in App.tsx, `/instellingen` is al
 * `RoleGuard allowedRoles="admin"`; er is geen aparte "super admin"-rol).
 *
 * Vier blokken als checklist (UX-spec S7): dry-run, impactrapport,
 * handleiding, activering. `Migratie doorvoeren` is pas enabled zodra alle
 * drie voorwaarden kloppen (server-autoritatief via
 * `GET /core-tasks/migration/status`'s `canActivate` -- dit component
 * herimplementeert de gate-logica niet zelf, het toont alleen wat de server
 * al berekend heeft, conform AD-2/AD-8's server-autoritatieve principe).
 *
 * Dit scherm is tijdelijk (verdwijnt/wordt read-only archief na livegang) —
 * functioneel gebouwd, bewust zonder overdreven toekomstbestendigheid.
 *
 * Geen shadcn/ui (dit project heeft dat niet geïnstalleerd, zie
 * `UitDienstDialog.tsx`/`PackageChangeDialog.tsx` Dev Notes) — custom
 * Tailwind, XXtract-kleurenpalet (navy #2F5A7A, teal #54949E, groen
 * #B7D945, rood #D64545 uitsluitend bij echte fouten, NFR-1).
 */

import { useEffect, useState } from 'react';
import {
  useMigrationStatus,
  useImpactReport,
  useRunDryRun,
  useRunCommit,
  useDeactivateMigration,
  useReviewImpactReport,
  usePublishManual,
} from '../useCoreTaskMigration';
import { coreTaskMigrationApi } from '../../../lib/coreTaskMigrationApi';
import { ApiRequestError } from '../../../lib/api';
import type { MigrationDryRunResult } from '@functieloonhuis/shared';

/** 1e van de eerstvolgende maand, als praktische default voor het migratiemoment-invoerveld. */
function defaultMigrationMoment(): string {
  const now = new Date();
  const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  return next.toISOString().slice(0, 10);
}

function errorMessage(error: unknown): string {
  return error instanceof ApiRequestError ? error.message : 'Er is een onbekende fout opgetreden';
}

export function CoreTaskMigrationTab() {
  const statusQuery = useMigrationStatus();

  if (statusQuery.isLoading) {
    return <div className="p-6 text-sm text-gray-500">Laden...</div>;
  }
  if (statusQuery.isError || !statusQuery.data) {
    return <div className="p-6 text-sm text-[#D64545]">Status kon niet geladen worden.</div>;
  }

  if (statusQuery.data.coreTasksActive) {
    return <ActiveReadOnlyView />;
  }

  return <ActiveChecklistView />;
}

// ---------------------------------------------------------------------------
// Read-only eindstatus (Task 7)
// ---------------------------------------------------------------------------

function ActiveReadOnlyView() {
  const statusQuery = useMigrationStatus();
  const deactivate = useDeactivateMigration();
  const [deactivateError, setDeactivateError] = useState<string | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  if (!statusQuery.data) return null;
  const status = statusQuery.data;

  async function handleDeactivate() {
    setDeactivateError(null);
    try {
      await deactivate.mutateAsync();
      setConfirmDeactivate(false);
    } catch (error) {
      setDeactivateError(errorMessage(error));
    }
  }

  return (
    <div data-testid="core-task-migration-readonly" className="p-6 space-y-4 max-w-3xl">
      <div className="flex items-center gap-2">
        <svg className="w-5 h-5 text-[#B7D945]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <h2 className="text-lg font-medium text-[#1E293B]">Migratie kerntaken is geactiveerd</h2>
      </div>
      <p className="text-sm text-[#1E293B]">
        Het nieuwe kerntaken-model (core_tasks_v2) is actief. Nieuwe beoordelingen gebruiken de kerntaken-flow. De
        checklist hierboven is niet langer nodig.
      </p>

      {/*
        Task 7 ("wanneer geactiveerd, door wie, dry-run/impactrapport-
        samenvatting") -- CR-followup: deze read-only weergave toonde
        voorheen alleen de generieke zin hierboven, zonder de door de story
        expliciet vereiste details. `activatedAt`/`activatedByName` zijn
        best-effort (zie coreTaskMigrationService.getLastActivationInfo) en
        kunnen `null` zijn (bv. vlak na een handmatige database-wijziging
        zonder audit-spoor) -- toon dan een neutrale "onbekend"-tekst i.p.v.
        niets te tonen.
      */}
      <div data-testid="activation-summary" className="rounded-md border border-[#E2E8F0] bg-gray-50 p-4 text-sm text-[#1E293B] space-y-1">
        <p>
          <span className="font-medium">Geactiveerd op:</span>{' '}
          {status.activatedAt ? new Date(status.activatedAt).toLocaleString('nl-NL') : 'onbekend'}
        </p>
        <p>
          <span className="font-medium">Geactiveerd door:</span> {status.activatedByName ?? 'onbekend'}
        </p>
        <p>
          <span className="font-medium">Dry-run bij activering:</span> {status.dryRun.valid}/{status.dryRun.total} geldig
        </p>
        <p>
          <span className="font-medium">Impactrapport gereviewd door:</span> {status.impactReportReviewed.by ?? 'onbekend'}
          {status.impactReportReviewed.at ? ` (${new Date(status.impactReportReviewed.at).toLocaleString('nl-NL')})` : ''}
        </p>
      </div>

      {status.hasDefinitiveV2Evaluation ? (
        <p data-testid="deactivate-locked-tooltip" className="text-sm text-gray-500" title="Er bestaat al een definitieve beoordeling onder het nieuwe model; terugdraaien is daarna niet meer mogelijk">
          Er bestaat al een definitieve beoordeling onder het nieuwe model — terugdraaien is niet meer mogelijk.
        </p>
      ) : (
        <div>
          {!confirmDeactivate ? (
            <button
              type="button"
              data-testid="deactivate-migration-link"
              onClick={() => setConfirmDeactivate(true)}
              className="text-sm text-[#54949E] hover:underline"
            >
              Activering ongedaan maken
            </button>
          ) : (
            <div className="mt-2 p-3 rounded-md border border-[#E2E8F0] bg-gray-50 max-w-md">
              <p className="text-sm text-[#1E293B] mb-2">
                Weet u zeker dat u de activering ongedaan wilt maken? Nieuwe beoordelingen gebruiken dan weer het oude
                model. Bestaande tijdvakken blijven staan.
              </p>
              {deactivateError && <p className="text-sm text-[#D64545] mb-2">{deactivateError}</p>}
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="deactivate-confirm"
                  onClick={handleDeactivate}
                  disabled={deactivate.isPending}
                  className="px-3 py-1.5 text-sm font-medium text-white rounded-md bg-[#D64545] hover:bg-[#D64545]/90 disabled:opacity-50"
                >
                  {deactivate.isPending ? 'Bezig...' : 'Ja, deactiveren'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDeactivate(false)}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 rounded-md border border-[#E2E8F0] hover:bg-gray-50"
                >
                  Annuleren
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Actieve checklist (Blok 1-4)
// ---------------------------------------------------------------------------

function ActiveChecklistView() {
  const statusQuery = useMigrationStatus();
  const [dryRunResult, setDryRunResult] = useState<MigrationDryRunResult | null>(null);
  const [impactReportGenerated, setImpactReportGenerated] = useState(false);

  if (!statusQuery.data) return null;
  const status = statusQuery.data;

  return (
    <div data-testid="core-task-migration-checklist" className="p-6 space-y-6 max-w-4xl">
      <div>
        <h1 className="text-xl font-semibold text-[#1E293B]">Migratie kerntaken</h1>
        <p className="mt-1 text-sm text-gray-500">
          Doorloop de checklist hieronder. Activering is pas mogelijk zodra alle drie voorwaarden zijn voldaan.
        </p>
      </div>

      <DryRunBlock onResult={setDryRunResult} />
      <ImpactReportBlock reviewed={status.impactReportReviewed} onGenerated={() => setImpactReportGenerated(true)} />
      <ManualBlock published={status.manualPublished} />
      <ActivationBlock status={status} dryRunResult={dryRunResult} impactReportGenerated={impactReportGenerated} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Blok 1 — Dry-run
// ---------------------------------------------------------------------------

function DryRunBlock({ onResult }: { onResult: (result: MigrationDryRunResult) => void }) {
  const [migrationMoment, setMigrationMoment] = useState(defaultMigrationMoment());
  const dryRun = useRunDryRun();
  const [result, setResult] = useState<MigrationDryRunResult | null>(null);

  async function handleRun() {
    const data = await dryRun.mutateAsync(migrationMoment);
    setResult(data);
    onResult(data);
  }

  return (
    <section data-testid="migration-block-dryrun" className="border border-[#E2E8F0] rounded-lg p-4 bg-white">
      <h2 className="text-base font-medium text-[#1E293B] mb-3">1. Dry-run</h2>

      <div className="flex items-end gap-3 mb-4">
        <div>
          <label htmlFor="migration-moment-input" className="block text-xs font-medium text-gray-700 mb-1">
            Migratiemoment (1e van de maand)
          </label>
          <input
            id="migration-moment-input"
            type="date"
            data-testid="migration-moment-input"
            value={migrationMoment}
            onChange={(e) => setMigrationMoment(e.target.value)}
            className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#54949E]"
          />
        </div>
        <button
          type="button"
          data-testid="run-dry-run-button"
          onClick={handleRun}
          disabled={dryRun.isPending}
          className="px-4 py-2 text-sm font-medium text-white rounded-md bg-[#54949E] hover:bg-[#54949E]/90 disabled:opacity-50"
        >
          {dryRun.isPending ? 'Bezig...' : 'Dry-run uitvoeren'}
        </button>
      </div>

      {dryRun.isError && <p className="text-sm text-[#D64545] mb-3">{errorMessage(dryRun.error)}</p>}

      {result && (
        <div>
          <p data-testid="dry-run-summary" className="text-sm font-medium text-[#1E293B] mb-2">
            {result.summary.valid}/{result.summary.total} geldig
            {result.summary.invalid > 0 && <span className="text-[#D64545]"> ({result.summary.invalid} met validatiefout)</span>}
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-[#E2E8F0]">
                  <th className="py-2 pr-4">Medewerker</th>
                  <th className="py-2 pr-4">Oud pakket</th>
                  <th className="py-2 pr-4">Nieuw pakket</th>
                  <th className="py-2 pr-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {result.employees.map((row) => (
                  <tr key={row.employeeName} data-testid={`dry-run-row-${row.employeeName}`} className="border-b border-[#E2E8F0] last:border-b-0">
                    <td className="py-2 pr-4 text-[#1E293B]">{row.employeeName}</td>
                    <td className="py-2 pr-4 text-gray-600">
                      {row.oldPackage.taskCount === null ? 'onbekend (medewerker niet gevonden)' : `${row.oldPackage.taskCount} taken`}
                    </td>
                    <td className="py-2 pr-4 text-gray-600">
                      {row.newPackage.exception === 'geen_pakket_besluit' ? (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">Geen pakket (besluit)</span>
                      ) : (
                        row.newPackage.assignments.map((a) => `${a.coreTaskName ?? a.coreTaskCode} ${a.timePercentage}%`).join(' · ')
                      )}
                      {row.status === 'already_migrated' && (
                        <>
                          <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-xs bg-[#54949E]/10 text-[#54949E]">al gemigreerd</span>
                          {/*
                            Task 3 ("'al gemigreerd'-diff-weergave... zodat
                            een vóór de migratie handmatig aangemaakt pakket
                            dat afwijkt van het besloten pakket zichtbaar
                            is") -- CR-followup: deze diff werd voorheen
                            door de backend wel berekend
                            (`alreadyMigratedDiff`) maar nergens getoond,
                            dus een admin kon niet zien of het bestaande
                            tijdvak afweek van het seed-besluit. Alleen
                            getoond als er daadwerkelijk een verschil is
                            (geen ruis bij een exacte match).
                          */}
                          {row.alreadyMigratedDiff &&
                            (row.alreadyMigratedDiff.seedOnlyAssignments.length > 0 || row.alreadyMigratedDiff.existingOnlyAssignments.length > 0) && (
                              <div data-testid={`already-migrated-diff-${row.employeeName}`} className="mt-1 text-xs text-gray-500">
                                <span className="font-medium text-[#D64545]">Wijkt af van het seed-pakket:</span>
                                {row.alreadyMigratedDiff.existingOnlyAssignments.length > 0 && (
                                  <div>
                                    Bestaand tijdvak heeft:{' '}
                                    {row.alreadyMigratedDiff.existingOnlyAssignments.map((a) => `${a.coreTaskName} ${a.timePercentage}%`).join(', ')}
                                  </div>
                                )}
                                {row.alreadyMigratedDiff.seedOnlyAssignments.length > 0 && (
                                  <div>
                                    Seed-pakket heeft:{' '}
                                    {row.alreadyMigratedDiff.seedOnlyAssignments.map((a) => `${a.coreTaskName} ${a.timePercentage}%`).join(', ')}
                                  </div>
                                )}
                              </div>
                            )}
                        </>
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      {row.validationErrors.length === 0 ? (
                        <span className="text-[#B7D945]" aria-label="geldig">
                          &#10003;
                        </span>
                      ) : (
                        <span className="text-[#D64545] text-xs">{row.validationErrors.join('; ')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {result.expectedUitDienstChecks.length > 0 && (
            <div className="mt-3 space-y-1">
              {result.expectedUitDienstChecks.map((check) => (
                <p
                  key={check.searchName}
                  data-testid={`uit-dienst-check-${check.searchName}`}
                  className={`text-xs ${check.ok ? 'text-gray-500' : 'text-[#D64545]'}`}
                >
                  {check.message}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Blok 2 — Impactrapport
// ---------------------------------------------------------------------------

function ImpactReportBlock({
  reviewed,
  onGenerated,
}: {
  reviewed: { done: boolean; by: string | null; at: string | null };
  onGenerated: () => void;
}) {
  const [enabled, setEnabled] = useState(false);
  const impactReportQuery = useImpactReport(enabled);
  const review = useReviewImpactReport();
  const [reviewerName, setReviewerName] = useState('');
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    if (impactReportQuery.data) {
      onGenerated();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impactReportQuery.data]);

  async function handleExport() {
    setExportError(null);
    try {
      const blob = await coreTaskMigrationApi.exportImpactReportCsv();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `impactrapport-kerntaken-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'Export mislukt');
    }
  }

  return (
    <section data-testid="migration-block-impact-report" className="border border-[#E2E8F0] rounded-lg p-4 bg-white">
      <h2 className="text-base font-medium text-[#1E293B] mb-3">2. Impactrapport</h2>

      <div className="flex gap-2 mb-4">
        <button
          type="button"
          data-testid="generate-impact-report-button"
          onClick={() => setEnabled(true)}
          disabled={impactReportQuery.isFetching}
          className="px-4 py-2 text-sm font-medium text-white rounded-md bg-[#54949E] hover:bg-[#54949E]/90 disabled:opacity-50"
        >
          {impactReportQuery.isFetching ? 'Bezig...' : 'Impactrapport genereren'}
        </button>
        {impactReportQuery.data && (
          <button
            type="button"
            data-testid="export-impact-report-button"
            onClick={handleExport}
            className="px-4 py-2 text-sm font-medium text-[#1E293B] rounded-md border border-[#E2E8F0] hover:bg-gray-50"
          >
            Exporteren (CSV)
          </button>
        )}
      </div>

      {exportError && <p className="text-sm text-[#D64545] mb-3">{exportError}</p>}
      {impactReportQuery.isError && <p className="text-sm text-[#D64545] mb-3">{errorMessage(impactReportQuery.error)}</p>}

      {impactReportQuery.data && (
        <div>
          <div className="overflow-x-auto mb-3">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-[#E2E8F0]">
                  <th className="py-2 pr-4">Medewerker</th>
                  <th className="py-2 pr-4">Oude totaalscore</th>
                  <th className="py-2 pr-4">Nieuwe totaalscore</th>
                  <th className="py-2 pr-4">Delta</th>
                </tr>
              </thead>
              <tbody>
                {impactReportQuery.data.employees.map((row) => (
                  <tr key={row.employeeId} className="border-b border-[#E2E8F0] last:border-b-0">
                    <td className="py-2 pr-4 text-[#1E293B]">{row.employeeName}</td>
                    <td className="py-2 pr-4 text-gray-600">{row.oudeTotaalscore}</td>
                    <td className="py-2 pr-4 text-gray-600">{row.nieuweTotaalscore}</td>
                    {/* Bewust kleurloos (UX-spec S7): een delta is geen fout of succes. */}
                    <td className="py-2 pr-4 text-[#1E293B]">
                      {row.delta >= 0 ? '+' : ''}
                      {row.delta}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p data-testid="impact-report-sm-c2" className="text-sm text-[#1E293B] mb-2">
            Gemiddelde vóór: {impactReportQuery.data.gemiddeldeOud} · Gemiddelde na: {impactReportQuery.data.gemiddeldeNieuw}
          </p>
          <p className="text-xs text-gray-500 mb-4">{impactReportQuery.data.projectieregel}</p>

          {reviewed.done ? (
            <p data-testid="impact-report-reviewed-status" className="text-sm text-[#B7D945]">
              Impactrapport gereviewd door {reviewed.by} ({reviewed.at ? new Date(reviewed.at).toLocaleString('nl-NL') : ''})
            </p>
          ) : (
            <div className="flex items-end gap-2">
              <div>
                <label htmlFor="reviewer-name-input" className="block text-xs font-medium text-gray-700 mb-1">
                  Naam reviewer
                </label>
                <input
                  id="reviewer-name-input"
                  type="text"
                  data-testid="reviewer-name-input"
                  value={reviewerName}
                  onChange={(e) => setReviewerName(e.target.value)}
                  className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
                  placeholder="Naam"
                />
              </div>
              <button
                type="button"
                data-testid="review-impact-report-button"
                onClick={() => review.mutate(reviewerName)}
                disabled={reviewerName.trim().length === 0 || review.isPending}
                className="px-4 py-2 text-sm font-medium text-white rounded-md bg-[#2F5A7A] hover:bg-[#2F5A7A]/90 disabled:opacity-50"
              >
                Impactrapport gereviewd
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Blok 3 — Handleiding
// ---------------------------------------------------------------------------

function ManualBlock({ published }: { published: { done: boolean; by: string | null; at: string | null } }) {
  const publish = usePublishManual();
  const [publisherName, setPublisherName] = useState('');

  return (
    <section data-testid="migration-block-manual" className="border border-[#E2E8F0] rounded-lg p-4 bg-white">
      <h2 className="text-base font-medium text-[#1E293B] mb-3">3. Handleiding</h2>

      {published.done ? (
        <p data-testid="manual-published-status" className="text-sm text-[#B7D945]">
          Handleiding herschreven en gepubliceerd door {published.by} ({published.at ? new Date(published.at).toLocaleString('nl-NL') : ''})
        </p>
      ) : (
        <div className="flex items-end gap-2">
          <div>
            <label htmlFor="publisher-name-input" className="block text-xs font-medium text-gray-700 mb-1">
              Naam
            </label>
            <input
              id="publisher-name-input"
              type="text"
              data-testid="publisher-name-input"
              value={publisherName}
              onChange={(e) => setPublisherName(e.target.value)}
              className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
              placeholder="Naam"
            />
          </div>
          <button
            type="button"
            data-testid="publish-manual-checkbox"
            onClick={() => publish.mutate(publisherName)}
            disabled={publisherName.trim().length === 0 || publish.isPending}
            className="px-4 py-2 text-sm font-medium text-white rounded-md bg-[#2F5A7A] hover:bg-[#2F5A7A]/90 disabled:opacity-50"
          >
            Handleiding herschreven en gepubliceerd
          </button>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Blok 4 — Activering
// ---------------------------------------------------------------------------

/**
 * Story TD-3 (tech-debt-followup): `canActivate` op de backend wordt uit ZES
 * condities berekend (`coreTaskMigrationService.ts`: dryRunGreen,
 * impactReportReviewed.done, manualPublished.done, !coreTasksActive,
 * !hasV2Final, !openV1EvaluationsExist). Deze functie checkte voorheen maar
 * drie daarvan, waardoor `canActivate === false` door één van de overige drie
 * een lege tooltip-tekst opleverde ("Nog niet mogelijk: .").
 *
 * `!coreTasksActive` wordt hier BEWUST niet gecheckt: `ActivationBlock`
 * (en dus deze functie) wordt uitsluitend gerenderd binnen
 * `ActiveChecklistView`, die alleen zichtbaar is wanneer
 * `!statusQuery.data.coreTasksActive` (zie `CoreTaskMigrationTab()` hierboven)
 * -- die conditie kan in deze component dus nooit de reden zijn dat de knop
 * disabled is, en hoeft niet in de tekst te verschijnen.
 */
function missingConditionsText(
  canActivate: boolean,
  status: {
    dryRun: { green: boolean };
    impactReportReviewed: { done: boolean };
    manualPublished: { done: boolean };
    hasDefinitiveV2Evaluation: boolean;
    openV1EvaluationsExist: boolean;
  }
): string {
  if (canActivate) return '';
  const missing: string[] = [];
  if (!status.dryRun.green) missing.push('dry-run is nog niet volledig groen');
  if (!status.impactReportReviewed.done) missing.push('impactrapport is nog niet gereviewd');
  if (!status.manualPublished.done) missing.push('handleiding is nog niet gepubliceerd');
  if (status.hasDefinitiveV2Evaluation) missing.push('er bestaat al een definitieve beoordeling onder het nieuwe model');
  if (status.openV1EvaluationsExist) missing.push('er zijn nog openstaande beoordelingen onder het oude model');
  return `Nog niet mogelijk: ${missing.join(', ')}.`;
}

function ActivationBlock({
  status,
  dryRunResult,
  impactReportGenerated,
}: {
  status: {
    canActivate: boolean;
    dryRun: { green: boolean };
    impactReportReviewed: { done: boolean };
    manualPublished: { done: boolean };
    hasDefinitiveV2Evaluation: boolean;
    openV1EvaluationsExist: boolean;
  };
  dryRunResult: MigrationDryRunResult | null;
  impactReportGenerated: boolean;
}) {
  const commit = useRunCommit();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [commitError, setCommitError] = useState<string | null>(null);

  // dryRunResult wordt gebruikt om het exacte migrationMoment van de laatst
  // uitgevoerde dry-run te hergebruiken bij de commit (consistente datum
  // tussen blok 1 en blok 4). `impactReportGenerated` wordt niet apart
  // gebruikt in de gate-conditie (die is server-autoritatief, zie
  // `status.canActivate`) maar bevestigt dat blok 2 minstens 1x is bezocht.
  void impactReportGenerated;

  const disabled = !status.canActivate;
  const tooltip = missingConditionsText(status.canActivate, status);
  // Review-fix: de bevestigingsdialoog toont expliciet welke datum
  // daadwerkelijk gecommit wordt (i.p.v. alleen generieke tekst) -- zowel
  // om AC "herhaalt het migratiemoment-besluit" letterlijker na te leven
  // als om te voorkomen dat een admin de dry-run-datum in blok 1 wijzigt
  // ZONDER opnieuw op "Dry-run uitvoeren" te klikken en dan in de
  // veronderstelling verkeert dat de nieuwe datum gecommit wordt.
  const commitMigrationMoment = dryRunResult?.migrationMoment ?? defaultMigrationMoment();

  async function handleConfirm() {
    setCommitError(null);
    try {
      await commit.mutateAsync(commitMigrationMoment);
      setConfirmOpen(false);
    } catch (error) {
      setCommitError(errorMessage(error));
    }
  }

  return (
    <section data-testid="migration-block-activation" className="border border-[#E2E8F0] rounded-lg p-4 bg-white">
      <h2 className="text-base font-medium text-[#1E293B] mb-3">4. Activering</h2>

      <span className="relative group inline-block" title={disabled ? tooltip : undefined}>
        <button
          type="button"
          data-testid="activate-migration-button"
          aria-disabled={disabled}
          onClick={() => !disabled && setConfirmOpen(true)}
          className={`px-4 py-2 text-sm font-medium text-white rounded-md bg-[#2F5A7A] hover:bg-[#2F5A7A]/90 ${
            disabled ? 'opacity-40 cursor-not-allowed' : ''
          }`}
        >
          Migratie doorvoeren
        </button>
      </span>
      {disabled && (
        <p data-testid="activate-migration-tooltip" className="mt-2 text-xs text-gray-500">
          {tooltip}
        </p>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40" data-testid="activate-confirm-overlay">
          <div role="dialog" aria-modal="true" data-testid="activate-confirm-dialog" className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-medium text-[#1E293B] mb-3">Migratie doorvoeren</h3>
            <p className="text-sm text-[#1E293B] mb-4">
              Dit start de nieuwe beoordelingsperiode per{' '}
              <strong data-testid="activate-confirm-moment">{commitMigrationMoment}</strong>: vanaf die datum
              gebruiken nieuwe beoordelingen het kerntaken-model (core_tasks_v2). Weet u zeker dat u dit wilt
              doorvoeren?
            </p>
            {commitError && (
              <p data-testid="activate-error" className="text-sm text-[#D64545] mb-3">
                {commitError}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={commit.isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 rounded-md border border-[#E2E8F0] hover:bg-gray-50 disabled:opacity-50"
              >
                Annuleren
              </button>
              <button
                type="button"
                data-testid="activate-confirm-button"
                onClick={handleConfirm}
                disabled={commit.isPending}
                className="px-4 py-2 text-sm font-medium text-white rounded-md bg-[#2F5A7A] hover:bg-[#2F5A7A]/90 disabled:opacity-50"
              >
                {commit.isPending ? 'Bezig...' : 'Bevestigen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
